import { useState, useMemo, useEffect, useCallback } from 'react'
import { Grid, Progress } from 'semantic-ui-react'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import { light, solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  Button,
  Card,
  GridRowColumn,
  Input,
  Text,
  Icon,
  Pill,
} from '../../../components/BaseComponents'
import { useReselector, useToggle } from '../../../utils/sharedHooks'
import { getFinancialProfile } from '../../../selectors/user.selectors'
import {
  dollarsToCents,
  centsToDollars,
  formatCurrencyFromCentsAndRound,
} from '../../../utils/currencyHelpers'
import {
  updateFinancialProfile,
  UPDATE_FINANCIAL_PROFILE_KEY,
} from '../../../actions/financialProfileActions'
import { useAppDispatch } from '../../../utils/typeHelpers'
import { getIsFetching } from '../../../reducers/fetch'
import { Colors } from '../../../styles/theme'
import {
  INSIGHTS_TYPE,
  getInsightsMonthYear,
} from '../../../components/Finances/shared/utils'
import {
  FETCH_INSIGHTS_SUMMARY_KEY,
  fetchInsightsSummary,
} from '../../../components/Finances/shared/financeActions'
import { useAnalyticsTrack, useAnalyticsView } from '../../Amplitude'
import { selectInsightSummary } from '@/reducers/insights.slice'
import { selectIsCatchUpBookkeepingComplete } from '../../CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'

const StyledProgress = styled(Progress)`
  &&&&&& {
    height: 9px;
    margin-bottom: 15px;
  }

  & > .bar {
    height: 9px !important;
    background-color: ${(props) => props.color || 'green'} !important;
  }
`

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  background-color: ${Colors.black};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`

// goes in the new dashboard, has slightly different design than original
const IncomeGoalCardV2 = () => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const financialProfile = useReselector(getFinancialProfile)
  const isCUBKComplete = useReselector(selectIsCatchUpBookkeepingComplete)
  const updateLoading = useReselector(
    getIsFetching,
    UPDATE_FINANCIAL_PROFILE_KEY(financialProfile?.id)
  )

  const [annualIncome, setAnnualIncome] = useState(
    financialProfile?.annualIncomeGoalInCents
      ? centsToDollars(financialProfile.annualIncomeGoalInCents).toString()
      : ''
  )
  const [editing, toggleEditing] = useToggle()

  const { month, year } = getInsightsMonthYear(DateTime.now())

  useEffect(() => {
    pageView('income goal progress card')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchInsightsSummary(INSIGHTS_TYPE.INCOME, month, year))
  }, [dispatch, month, year])

  const summaryDataResponse = useReselector(
    selectInsightSummary,
    FETCH_INSIGHTS_SUMMARY_KEY(INSIGHTS_TYPE.INCOME, month, year)
  )

  const handleTrack = useCallback(async () => {
    await updateFinancialProfile(financialProfile?.id, {
      annualIncomeGoalInCents: dollarsToCents(annualIncome),
    })(dispatch)
    if (editing) {
      toggleEditing()
    }
  }, [annualIncome, dispatch, editing, financialProfile?.id, toggleEditing])

  const showLever1EarlyAccessToCentralizedWeeklyDashboardMvp =
    useBooleanFlagValue(
      FEATURE_FLAG_KEYS.enableLever1EarlyAccessToCentralizedWeeklyDashboardMvp,
      false
    )

  const displayState = useMemo(() => {
    if (
      showLever1EarlyAccessToCentralizedWeeklyDashboardMvp &&
      !isCUBKComplete
    ) {
      return 'comingSoon'
    } else if (financialProfile?.annualIncomeGoalInCents === null) {
      return 'empty'
    } else if (editing) {
      return 'editing'
    } else {
      return 'progress'
    }
  }, [
    financialProfile,
    editing,
    isCUBKComplete,
    showLever1EarlyAccessToCentralizedWeeklyDashboardMvp,
  ])

  const incomeYearToDate = useMemo(() => {
    if (!summaryDataResponse?.[0]) return 0
    if (summaryDataResponse[0].ytd_total_in_cents === null) return 0
    return summaryDataResponse[0].ytd_total_in_cents
  }, [summaryDataResponse])

  const incomeGoalProgress = useMemo(() => {
    if (!financialProfile?.annualIncomeGoalInCents) return 0
    return Math.round(
      (incomeYearToDate / financialProfile.annualIncomeGoalInCents) * 100
    )
  }, [incomeYearToDate, financialProfile])

  const content = useMemo(() => {
    switch (displayState) {
      case 'comingSoon':
        return (
          <Grid.Row style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <IconWrapper>
              <Icon
                icon={solid('money-bill-trend-up')}
                size="lg"
                color="white"
              />
            </IconWrapper>
            <Text
              as="h3"
              style={{
                whiteSpace: 'nowrap',
              }}
            >
              Annual Income Goal
            </Text>
            <Text as="bodySm" color="charcoal">
              <span style={{ fontWeight: 500 }}>Coming soon!</span> You&apos;ll
              be able to set your Annual Income Goal after your catch-up
              bookkeeping is complete.{' '}
              <button
                onClick={() => {
                  document
                    .getElementsByClassName('inner-content-wrapper')[0]
                    ?.scrollIntoView({
                      behavior: 'smooth',
                    })
                }}
                style={{
                  color: Colors.charcoal,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  outline: 'none',
                }}
              >
                What do I need to do next?
              </button>
            </Text>
          </Grid.Row>
        )
      case 'empty':
        return (
          <Grid>
            <GridRowColumn>
              <Pill
                color="neutral"
                variant="light"
                style={{ marginBottom: 12 }}
              >
                <Icon icon={solid('sparkle')} style={{ marginRight: 8 }} />
                NEW
              </Pill>
              <Text as="h2">Track your annual income goals</Text>
            </GridRowColumn>
            <GridRowColumn>
              <Text>
                Enter your target annual income for {year} to track your
                progress here.
              </Text>
            </GridRowColumn>
            <GridRowColumn>
              <Input
                placeholder="$ Enter target income amount"
                value={annualIncome}
                onChange={(value) => setAnnualIncome(value)}
                componentType="currency"
              />
            </GridRowColumn>
            <GridRowColumn>
              <Button
                variant="secondary"
                onClick={() => {
                  handleTrack()
                  track('clicked track button', {
                    button: 'track income',
                  })
                }}
                loading={updateLoading}
              >
                Track{' '}
                <Icon icon={light('arrow-right')} style={{ marginLeft: 8 }} />
              </Button>
            </GridRowColumn>
          </Grid>
        )
      case 'progress':
        return (
          <Grid>
            <GridRowColumn>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text as="h3">Annual Income Goal</Text>
                <Button
                  onClick={() => {
                    toggleEditing()
                    track('clicked edit button', {
                      button: 'edit income',
                    })
                  }}
                  variant="secondaryLink"
                >
                  Edit
                </Button>
              </div>
            </GridRowColumn>
            <GridRowColumn>
              <Text as="display2" color="green">
                {incomeGoalProgress}%
              </Text>
              <Text as="bodyXs" color="darkGray">
                towards your {year} target annual income
              </Text>
            </GridRowColumn>
            <GridRowColumn short>
              <StyledProgress
                percent={incomeGoalProgress}
                size="small"
                color={Colors.green}
              />
              <Text as="bodySm">
                <span style={{ color: Colors.green }}>
                  {formatCurrencyFromCentsAndRound(incomeYearToDate, {
                    hideDecimalsIfZero: true,
                  })}{' '}
                </span>{' '}
                /{' '}
                {formatCurrencyFromCentsAndRound(
                  financialProfile?.annualIncomeGoalInCents,
                  { hideDecimalsIfZero: true }
                )}
              </Text>
            </GridRowColumn>
          </Grid>
        )
      case 'editing':
        return (
          <Grid>
            <GridRowColumn>
              <Text as="h2">Annual Income Goal</Text>
            </GridRowColumn>
            <GridRowColumn>
              <Text as="h3">{year} Target Annual Income</Text>
              <Text as="bodyXs" style={{ marginTop: 8, marginBottom: 8 }}>
                Enter a new annual income goal. We&apos;ll update your progress
                to reflect the new target.
              </Text>
              <Input
                placeholder="$ Enter target income amount"
                value={annualIncome}
                onChange={(value) => setAnnualIncome(value)}
                componentType="currency"
              />
            </GridRowColumn>
            <GridRowColumn
              verticalAlign="middle"
              columnStyle={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
              }}
            >
              <Button
                onClick={() => {
                  handleTrack()
                  track('clicked update goal button', {
                    button: 'update goal',
                  })
                }}
                loading={updateLoading}
              >
                Update Goal
              </Button>

              <Button
                onClick={toggleEditing}
                loading={updateLoading}
                variant="actionLink"
              >
                Cancel
              </Button>
            </GridRowColumn>
          </Grid>
        )
      default:
        return displayState satisfies never
    }
  }, [
    annualIncome,
    displayState,
    financialProfile?.annualIncomeGoalInCents,
    handleTrack,
    incomeGoalProgress,
    incomeYearToDate,
    toggleEditing,
    track,
    updateLoading,
    year,
  ])

  return (
    <Card
      type="subsection"
      backgroundColor={
        displayState === 'progress' || displayState === 'comingSoon'
          ? 'stone40'
          : 'natural'
      }
    >
      {content}
    </Card>
  )
}

export default IncomeGoalCardV2
